import { Box, Button, Divider, List } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FormattedMessage } from 'react-intl';
import {
  useIntersectionObserver,
  ResourcesCarousel,
  ResourcesHeader,
  FeaturedResourcesItem,
  LoadingIndicator,
  Routes,
  FunctionBuilder,
} from '@xolv-mybrightlink/common';
import { useFetchArticles } from '../hooks/useFetchArticles';
import { useArticleCategories } from '../hooks/useArticleCategories';
import { Article } from '../models/ArticleResult';
import { useArticleByCategory } from '../hooks/useArticleContext';
import { useBookmarkArticle } from '../hooks/useBookmarkArticle';
import { ArticleItem } from '../components/ArticleItem';

export const ArticlePage = () => {
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const categories = useArticleCategories();
  const navigate = useNavigate();
  const { categoryId = 'all-articles' } = useParams<{ categoryId: string }>();
  const fetchArticles = useFetchArticles(categoryId);
  const bookmarkArticle = useBookmarkArticle();
  const { initial, articles, error, loading } =
    useArticleByCategory(categoryId);

  useIntersectionObserver(loadMoreRef, fetchArticles);

  const openCategory = useCallback(
    (index: number) => {
      navigate(
        index === 0
          ? Routes.resources.articles.index
          : Routes.resources.articles.categoryId(categories[index].databaseId),
      );
    },
    [navigate, categories],
  );

  const openArticleDetails = useCallback(
    (article: Article) => {
      navigate(
        Routes.resources.articles.slug(
          article.slug,
          categoryId ?? 'all-articles',
        ),
      );
    },
    [categoryId, navigate],
  );

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pl: 7.5,
            pr: 7.5,
            pt: 10,
          }}
        >
          <Typography
            onClick={() => openCategory(0)}
            sx={{
              fontSize: 20,
              fontWeight: 500,
              color: '#3b4559',
              cursor: 'pointer',
            }}
          >
            <FormattedMessage id='home.label.articles' />
          </Typography>
        </Box>
        <ResourcesHeader
          categories={categories.map((articleCategory) => ({
            id: articleCategory.databaseId,
            name: articleCategory.name,
          }))}
          selectedCategoryId={Number(categoryId)}
          onCategoryClicked={openCategory}
        />
        <Divider
          sx={{
            ml: 7.5,
            mr: 7.5,
          }}
        />
      </Box>
      <Box
        className='hide-scrollbar'
        sx={{
          height: '100%',
          overflowY: 'auto',
          justifyContent: 'center',
        }}
      >
        <FunctionBuilder>
          {() => {
            if (error) {
              return <></>;
            } else if (!initial && articles.length === 0) {
              return (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    pt: 30,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <img src='/public/assets/images/dog_empty.svg' width='60%' />
                  <Typography
                    sx={{
                      pt: 10,
                    }}
                  >
                    <FormattedMessage id='resources.article.empty' />
                  </Typography>
                  <Button
                    variant='contained'
                    disableElevation
                    onClick={() => navigate(-1)}
                    sx={{
                      mt: 10,
                      borderRadius: 30,
                      py: 3.5,
                      width: '30%',
                    }}
                  >
                    <FormattedMessage id='common.go-back' />
                  </Button>
                </Box>
              );
            } else {
              return (
                <>
                  {articles.length > 0 && (
                    <ResourcesCarousel>
                      {articles.slice(0, 3).map((article) => (
                        <FeaturedResourcesItem
                          key={article.slug}
                          name={article.title}
                          imgUrl={article.featuredImage.node.sourceUrl}
                          onItemClicked={() => openArticleDetails(article)}
                          content={article.content}
                        />
                      ))}
                    </ResourcesCarousel>
                  )}
                  <List
                    sx={{
                      pl: 7.5,
                      pr: 7.5,
                    }}
                  >
                    {articles.map((article) => (
                      <ArticleItem
                        key={article.id}
                        article={article}
                        onArticleClicked={() => openArticleDetails(article)}
                        onBookmarkClicked={async (event) => {
                          event.stopPropagation();
                          await bookmarkArticle(article.databaseId, categoryId);
                        }}
                      />
                    ))}
                  </List>
                  <div style={{ height: '1px' }} ref={loadMoreRef} />
                  {loading && (
                    <LoadingIndicator
                      sx={{
                        height: initial ? '100%' : undefined,
                        py: 10,
                      }}
                    />
                  )}
                </>
              );
            }
          }}
        </FunctionBuilder>
      </Box>
    </Box>
  );
};
